import React from 'react';
import { UserGroupIcon, HandRaisedIcon, GlobeAmericasIcon } from '@heroicons/react/24/outline';

interface EligibilityItem {
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  borderColor: string;
  hoverBg: string;
}

const eligibilityItems: EligibilityItem[] = [
  {
    title: "Who Can Participate",
    description: "This hackathon is open to students, professionals, and early-stage startups who are passionate about building data applications with Python. Whether you're a seasoned developer or just starting your coding journey, we welcome your participation!",
    icon: UserGroupIcon,
    color: 'text-cyan-500',
    borderColor: 'border-cyan-500',
    hoverBg: 'hover:bg-cyan-50'
  },
  {
    title: "Team Size",
    description: "You can participate as an individual or form a team of up to 4 members. While teams can bring diverse skills and perspectives, solo participants are equally welcome. We'll help you find teammates if you're looking for one!",
    icon: HandRaisedIcon,
    color: 'text-yellow-500',
    borderColor: 'border-yellow-500',
    hoverBg: 'hover:bg-yellow-50'
  },
  {
    title: "Global Participation",
    description: "This is a global hackathon! We welcome participants from all around the world. All submissions and communications will be in English, and the hackathon timeline is based on UTC to accommodate participants from different time zones.",
    icon: GlobeAmericasIcon,
    color: 'text-purple-500',
    borderColor: 'border-purple-500',
    hoverBg: 'hover:bg-purple-50'
  }
];

const HackathonEligibility: React.FC = () => {
  return (
    <div className="relative max-w-8xl mx-auto px-4 py-16">
      {/* Semi-transparent overlay */}
      <div className="absolute inset-0 bg-white/80 backdrop-blur-md" />
      
      <div className="relative z-10">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          Who It's For
        </h2>
        
        <div className="flex flex-col md:flex-row justify-center items-stretch gap-8">
          {eligibilityItems.map((item, index) => (
            <div
              key={index}
              className="w-full md:w-[350px] group"
            >
              <div className={`flex flex-col items-center bg-white/50 p-6 border-2 ${item.borderColor} ${item.hoverBg}
                transition-all duration-300 ease-in-out
                hover:shadow-lg hover:-translate-y-2 hover:rotate-2
                group-hover:border-opacity-100 border-opacity-50 h-full`}>
                {/* Icon */}
                <div className={`text-4xl mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 ${item.color}`}>
                  <item.icon className="w-12 h-12" />
                </div>
                
                {/* Text Content */}
                <div className="space-y-3 text-center flex-grow">
                  <h3 className="text-xl font-semibold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="text-slate-600">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-600">
            Not sure if you're eligible?{' '}
            <a
              href="mailto:hackathon@structuredlabs.com"
              className="text-slate-900 hover:text-slate-700 underline"
            >
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HackathonEligibility; 